import type { ButtonHTMLAttributes } from "react";

import { cn } from "@/shared/lib/cn";

/**
 * `primary`: the one thing the screen is for, in graphite. `outline`: the
 * next thing after it, when both stand together. `secondary`: everything
 * else. `danger`: an action that takes something away, where it is offered.
 * `destructive`: the same action where it is confirmed. `ghost`: a link that
 * is a button.
 */
export type ButtonVariant =
  | "primary"
  | "outline"
  | "secondary"
  | "danger"
  | "destructive"
  | "ghost";

const VARIANTS: Readonly<Record<ButtonVariant, string>> = {
  primary:
    "border-primary bg-primary text-on-primary shadow-xs hover:border-primary-strong hover:bg-primary-strong",
  outline: "border-fg bg-surface text-fg shadow-xs hover:bg-head",
  secondary: "border-line-strong bg-surface text-fg shadow-xs hover:bg-head",
  danger: "border-danger-line bg-surface text-danger hover:bg-danger-tint",
  destructive: "border-danger-solid bg-danger-solid text-on-danger hover:opacity-90",
  ghost: "border-transparent text-accent hover:bg-accent-tint",
};

/** `md`: 34 px, where a screen's actions stand. `sm`: 28 px, inside a row, a card's heading, a panel. */
export type ButtonSize = "md" | "sm";

const SIZES: Readonly<Record<ButtonSize, string>> = {
  md: "h-[34px] gap-[7px] px-3",
  sm: "h-7 gap-1.5 px-[9px] text-sm",
};

/** The look alone, for a link that should read as a button. */
export function buttonClass(
  variant: ButtonVariant = "secondary",
  className?: string,
  size: ButtonSize = "md",
): string {
  return cn(
    "inline-flex shrink-0 items-center justify-center rounded-control border font-medium whitespace-nowrap transition-colors",
    "disabled:cursor-not-allowed disabled:opacity-55 aria-disabled:cursor-not-allowed aria-disabled:opacity-55",
    SIZES[size],
    VARIANTS[variant],
    className,
  );
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /**
   * The action is under way: the button says so and cannot be pressed again.
   * It keeps the keyboard, though: a busy button is `aria-disabled`, not
   * `disabled`, which would drop the focus to the page and leave whoever
   * pressed it to find their way back after a failure.
   */
  busy?: boolean;
}

export function Button({
  variant = "secondary",
  size = "md",
  busy = false,
  disabled,
  type = "button",
  className,
  children,
  onClick,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled}
      aria-disabled={(busy && disabled !== true) || undefined}
      aria-busy={busy || undefined}
      className={buttonClass(variant, className, size)}
      onClick={(event) => {
        // Pressed while busy: nothing, a form's submit included.
        if (busy) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }}
      {...rest}
    >
      {busy && <Spinner />}
      {children}
    </button>
  );
}

function Spinner() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      className="animate-spin"
    >
      <path d="M12 3a9 9 0 1 0 9 9" />
    </svg>
  );
}
